/**
 * @page /portal/statistics
 * @description Kunden-Portal Outbound-Statistiken — Pakete, SKUs, Artikel, Gewicht
 *   mit KPI-Cards, 12-Monats-Balkenchart und Detail-Tabelle.
 * @features
 *   - 4 KPI-Cards (Pakete, SKUs, Artikel, Gewicht) mit Trend-Badges
 *   - 12-Monats-Balkendiagramm (SVG)
 *   - Monatliche Detail-Tabelle
 *   - AI-Forecast-Vorschlag Card
 * @api GET /api/portal/stats/overview, /api/portal/stats/monthly, /api/portal/stats/forecast-suggestion
 * @roles kunde
 * @i18n portal.stats
 */
import { useEffect, useState } from 'react';
import {
  BarChart as RechartsBarChart,
  Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useLanguage } from '../context/LanguageContext';
import { PageHeader } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';
const api = (path) => fetch(`${API}${path}`, { credentials: 'include' }).then(r => r.json());

const MONTH_NAMES_DE = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const MONTH_NAMES_EN = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function TrendBadge({ value }) {
  if (value == null) return <span className="text-xs text-[var(--text-secondary)]">–</span>;
  const isPos = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-mono font-medium ${isPos ? 'text-emerald-500' : 'text-red-500'}`}>
      {isPos ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
}

// Count weekdays (Mo-Fr) in a given month
function countWeekdays(year, month) {
  const dim = new Date(year, month, 0).getDate();
  let wd = 0;
  for (let d = 1; d <= dim; d++) {
    const dow = new Date(year, month - 1, d).getDay();
    if (dow >= 1 && dow <= 5) wd++;
  }
  return wd;
}

function BarChartTooltip({ active, payload, valueKey, lang, t }) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  const monthNamesFull = lang === 'de'
    ? ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
    : ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const fmt = (n) => (n || 0).toLocaleString('de-DE');
  return (
    <div className="bg-[var(--bg-elevated)] border border-[var(--border-card)] rounded-lg px-3 py-2 shadow-dropdown text-xs">
      <div className="font-medium text-content mb-1">{monthNamesFull[d.month]} {d.year}</div>
      {d.isCurrent && d.projection ? (
        <>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-sm bg-[#338edc]" />
            <span className="text-[var(--text-secondary)]">{t('portal.stats.soFar')}:</span>
            <span className="font-mono font-semibold text-content">{fmt(d[valueKey])}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-sm bg-[#338edc]/30 border border-[#338edc]/50" />
            <span className="text-[var(--text-secondary)]">{t('portal.stats.projection')}:</span>
            <span className="font-mono font-semibold text-content">~{fmt(d.projection)}</span>
          </div>
          <div className="text-[10px] text-[var(--text-tertiary)] mt-1">
            {d.active_days} {t('portal.stats.activeDays')} · {countWeekdays(d.year, d.month)} {t('portal.stats.workdaysTotal')}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-sm bg-[#338edc]" />
            <span className="font-mono font-semibold text-content">{fmt(d[valueKey])}</span>
            <span className="text-[var(--text-secondary)]">{t('portal.stats.packages')}</span>
          </div>
          {d.active_days != null && (
            <div className="text-[10px] text-[var(--text-secondary)] mt-1">{d.active_days} {t('portal.stats.activeDays')}</div>
          )}
        </>
      )}
    </div>
  );
}

function BarChart({ data, valueKey = 'packages', lang = 'en', t }) {
  if (!data || data.length === 0) return null;

  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth() + 1;
  const monthNames = lang === 'de' ? MONTH_NAMES_DE : MONTH_NAMES_EN;

  const enriched = data.map(d => {
    const isCurrent = d.year === curYear && d.month === curMonth;
    let projection = null;
    if (isCurrent && d.active_days > 0 && d[valueKey] > 0) {
      const totalWeekdays = countWeekdays(d.year, d.month);
      projection = Math.round((d[valueKey] / d.active_days) * totalWeekdays);
    }
    const projection_remainder = projection && projection > d[valueKey] ? projection - d[valueKey] : 0;
    return { ...d, isCurrent, projection, projection_remainder, monthLabel: monthNames[d.month] };
  });

  const fmtY = (v) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v;

  return (
    <div className="w-full" style={{ height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart data={enriched} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis dataKey="monthLabel" stroke="#6B7280" fontSize={12} />
          <YAxis stroke="#6B7280" fontSize={12} tickFormatter={fmtY} />
          <Tooltip
            cursor={{ fill: '#338edc', fillOpacity: 0.05 }}
            content={<BarChartTooltip valueKey={valueKey} lang={lang} t={t} />}
          />
          <Bar dataKey={valueKey} stackId="m" fill="#338edc" radius={[4, 4, 0, 0]} />
          <Bar dataKey="projection_remainder" stackId="m" fill="#338edc" fillOpacity={0.25} radius={[4, 4, 0, 0]} />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function PortalStats() {
  const { t, language } = useLanguage();
  const [overview, setOverview] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/api/portal/stats/overview'),
      api('/api/portal/stats/monthly'),
    ])
      .then(([o, m]) => { setOverview(o); setMonthly(m); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  const cur = overview?.current || {};
  const trend = overview?.trend || {};
  const months = monthly?.months || [];
  const lang = language || 'en';
  const monthNames = lang === 'de' ? MONTH_NAMES_DE : MONTH_NAMES_EN;

  const fmt = (n) => (n || 0).toLocaleString('de-DE');
  const fmtW = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}t` : `${(n || 0).toLocaleString('de-DE')} kg`;

  const fmtAvg = (n) => n == null ? '–' : n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const kpis = [
    { key: 'packages', label: t('portal.stats.packages'), value: fmt(cur.packages), trend: trend.packages },
    { key: 'skus', label: t('portal.stats.avgSkusPerOrder'), value: fmtAvg(cur.avg_skus), trend: trend.skus, sub: `${t('portal.stats.total')}: ${fmt(cur.skus)}` },
    { key: 'articles', label: t('portal.stats.avgArticlesPerOrder'), value: fmtAvg(cur.avg_articles), trend: trend.articles, sub: `${t('portal.stats.total')}: ${fmt(cur.articles)}` },
    { key: 'weight', label: t('portal.stats.avgWeightPerOrder'), value: `${fmtAvg(cur.avg_weight)} kg`, trend: trend.weight, sub: `${t('portal.stats.total')}: ${fmtW(cur.weight)}` },
  ];

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={t('portal.stats.title')} subtitle={t('portal.stats.subtitle')} />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map(kpi => (
          <div key={kpi.key} className="bg-surface-card border border-line rounded-card p-5">
            <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-3">{kpi.label}</div>
            <div className="flex items-end justify-between">
              <span className="text-xl font-semibold font-mono text-content">{kpi.value}</span>
              <TrendBadge value={kpi.trend} />
            </div>
            {kpi.sub && <div className="text-[10px] text-[var(--text-tertiary)] mt-1">{kpi.sub}</div>}
            <div className="text-[10px] text-[var(--text-secondary)] mt-1">{t('portal.stats.vsLastMonth')}</div>
          </div>
        ))}
      </div>

      {/* Bar Chart */}
      {months.length > 0 && (
        <div className="bg-surface-card border border-line rounded-card p-5">
          <h3 className="text-sm font-semibold text-content mb-3">{t('portal.stats.monthlyChart')}</h3>
          <BarChart data={months} valueKey="packages" lang={lang} t={t} />
          <div className="flex items-center gap-3 mt-2 justify-center">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-[#338edc] rounded-sm" />
              <span className="text-[10px] text-[var(--text-secondary)]">{t('portal.stats.packages')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Detail Table */}
      {months.length > 0 && (
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          <div className="px-5 py-3 border-b border-line">
            <h3 className="text-sm font-semibold text-content">{t('portal.stats.monthlyDetails')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.stats.month')}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.stats.packages')}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.stats.avgSkusPerOrder')}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.stats.avgArticlesPerOrder')}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.stats.avgWeightPerOrder')}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.stats.activeDays')}</th>
                </tr>
              </thead>
              <tbody>
                {[...months].reverse().map(m => (
                  <tr key={m.month_key} className="border-b border-line hover:bg-surface-hover">
                    <td className="px-4 py-3 text-xs font-medium text-content">{monthNames[m.month]} {m.year}</td>
                    <td className="px-4 py-3 text-xs text-content text-right font-mono">{fmt(m.packages)}</td>
                    <td className="px-4 py-3 text-xs text-content text-right font-mono">{fmtAvg(m.avg_skus)}</td>
                    <td className="px-4 py-3 text-xs text-content text-right font-mono">{fmtAvg(m.avg_articles)}</td>
                    <td className="px-4 py-3 text-xs text-content text-right font-mono">{fmtAvg(m.avg_weight)} kg</td>
                    <td className="px-4 py-3 text-xs text-[var(--text-secondary)] text-right font-mono">{m.active_days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {months.length === 0 && (
        <div className="bg-surface-card border border-line rounded-card p-10 text-center">
          <p className="text-sm text-[var(--text-secondary)]">{t('portal.stats.noData')}</p>
        </div>
      )}
    </div>
  );
}
